// Shared constants + client-side metric helpers for the Program Builder.
// Extracted from the previous monolithic ProgramSessionBuilder.jsx to keep
// the individual component files focused.
import {
  Fire, Lightning, Barbell, ArrowsClockwise, Heart, Wind, Waveform,
  ArrowsIn,
} from "@phosphor-icons/react";

export const SECTION_TYPES = [
  { id: "warmup",       label: "Warm-up",      icon: Fire,             color: "#F97316" },
  { id: "activation",   label: "Activation",   icon: Waveform,         color: "#EAB308" },
  { id: "speed",        label: "Speed",        icon: Wind,             color: "#06B6D4" },
  { id: "power",        label: "Power",        icon: Lightning,        color: "#A855F7" },
  { id: "strength",     label: "Strength",     icon: Barbell,          color: "#EF4444" },
  { id: "accessory",    label: "Accessory",    icon: ArrowsIn,         color: "#3B82F6" },
  { id: "conditioning", label: "Conditioning", icon: Heart,            color: "#22C55E" },
  { id: "recovery",     label: "Recovery",     icon: ArrowsClockwise,  color: "#14B8A6" },
];

export const SECTION_MAP = Object.fromEntries(SECTION_TYPES.map((s) => [s.id, s]));

export const SESSION_TYPE_MULT = {
  strength: 1.0, power: 1.2, speed: 0.8, conditioning: 0.6,
  mobility: 0.4, recovery: 0.3, taper: 0.5, return_to_play: 0.5,
};

// Progression schemes surfaced in the scheme dialog.
export const SCHEMES = [
  { id: "automatic",  label: "Automatic",  hint: "Coach picks nothing — reasonable default" },
  { id: "linear",     label: "Linear",     hint: "Add small % each week, RPE ~7-8" },
  { id: "wave",       label: "Wave",       hint: "3-week wave: build/build/deload" },
  { id: "block",      label: "Block",      hint: "Accumulation → intensification → realization" },
  { id: "undulating", label: "Undulating", hint: "Rotates volume/intensity across weeks" },
  { id: "custom",     label: "Coach Custom", hint: "You supply the grid" },
];

// Mesocycle finisher presets.
export const FINISHER_KINDS = [
  { id: "deload", label: "Deload", hint: "50% vol · 60% 1RM · RPE cap 7 — reduce fatigue, keep pattern" },
  { id: "peak",   label: "Peak",   hint: "40% vol · 90% 1RM · reps 1-3 · RPE 8-9 — sharpen and taper" },
];

/* ------------ Metric calculations ------------ */

export function calcSessionMetrics(exercises = [], sessionType = "strength") {
  let volume = 0, tonnage = 0, e1rmMax = 0, intensitySum = 0, intensityN = 0;
  (exercises || []).forEach((ex) => {
    const sets = Number(ex.sets) || 0;
    const reps = Number(String(ex.reps || "").split(/[-x×]/)[0]) || 0;
    const w = Number(ex.weight_kg) || 0;
    volume += sets * reps;
    tonnage += sets * reps * w;
    if (w && reps) {
      const e = w * (1 + reps / 30);
      if (e > e1rmMax) e1rmMax = e;
      if (ex.percentage_1rm) { intensitySum += Number(ex.percentage_1rm); intensityN += 1; }
      else if (e) { intensitySum += (w / e) * 100; intensityN += 1; }
    }
  });
  const mult = SESSION_TYPE_MULT[sessionType] || 1.0;
  return {
    volume: Math.round(volume),
    tonnage: Math.round(tonnage),
    e1rm: Math.round(e1rmMax),
    intensity: intensityN ? Math.round(intensitySum / intensityN) : 0,
    sessionLoad: Math.round(tonnage * mult),
  };
}

/* ------------ Grouping helper ------------ */

export function groupBySection(exercises = []) {
  const groups = {};
  (exercises || []).forEach((ex, idx) => {
    const s = (ex.section || "strength").toLowerCase();
    (groups[s] ||= []).push({ ...ex, __idx: idx });
  });
  return groups;
}

// Turn a favourite record into an exercise row ready to drop into a session.
export function favToExercise(f) {
  return {
    name: f.name,
    section: f.section || "strength",
    sets: f.sets || 3,
    reps: f.reps || "8",
    weight_kg: f.weight_kg || 0,
    percentage_1rm: f.percentage_1rm || null,
    rpe: f.rpe || "",
    tempo: f.tempo || "",
    rest_sec: f.rest_sec || 90,
    notes: f.notes || "",
    is_favorite: true,
  };
}

// Map an exercise_library row → session-ready exercise. Only fields the
// builder understands are copied; the rest live in the library detail view.
import { normalizeVideoUrl } from "@/lib/videoUrl";
export function libraryToExercise(lib) {
  // Map library `category` → builder `section`. Most seed categories map to
  // an existing SECTION_TYPES id one-to-one; the outliers get a best guess.
  const CATEGORY_TO_SECTION = {
    strength: "strength", power: "power", olympic: "power", plyometric: "power",
    speed: "speed", conditioning: "conditioning", mobility: "mobility",
    warmup: "warmup", activation: "warmup", core: "accessory", accessory: "accessory",
  };
  return {
    name: lib.name,
    section: CATEGORY_TO_SECTION[lib.category] || "strength",
    sets: 3,
    reps: "8",
    weight_kg: 0,
    percentage_1rm: null,
    rpe: "",
    tempo: lib.tempo || "",
    rest_sec: 90,
    notes: (lib.coach_cues && lib.coach_cues.length ? lib.coach_cues.join(" · ") : ""),
    video_url: normalizeVideoUrl(lib.video_url || ""),
    from_library: true,
    library_exercise_id: lib.exercise_id,
  };
}

